import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {profilePicSrc, INITIALS_SIZE} from '../../constants';
import ShortcutModalContent from '../shortcut_modal_content';
import Util from '../../forecast-app/shared/util/util';
import * as tracking from '../../tracking';
import LogoutMutation from '../../mutations/logout_mutation';
import LogoutSisenseUserMutation from '../../mutations/LogoutSisenseUserMutation';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import PersonInitials from '../../forecast-app/shared/components/person/person_initials';
import {hasPermission, isClientUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {AdminIcon, HelpIcon, KeyboardIcon, LogOffIcon, ProfileIcon, TimerIcon, WhatIsNewIcon} from 'web-components/icons';
import Styled from 'styled-components';

class ProfileMenu extends Component {
	constructor(props) {
		super(props);

		this.state = {
			expanded: false,
			focusProfileRef: false,
		};
	}

	componentDidUpdate() {
		if (this.state.focusProfileRef) {
			this.profileRef.focus();
			this.setState({focusProfileRef: false});
		}
	}

	handleLogout() {
		const onSuccess = ({login}) => {
			tracking.logout();
			window.location.href = window.location.origin;
		};
		if (this.props.viewer.sisenseUserId) {
			// Log user out of Sisense
			Util.CommitMutation(LogoutSisenseUserMutation, {sisenseUserId: this.props.viewer.sisenseUserId}, () => {
				Util.CommitMutation(LogoutMutation, null, onSuccess);
				this.setState({expanded: false});
			});
		} else {
			Util.CommitMutation(LogoutMutation, null, onSuccess);
			this.setState({expanded: false});
		}
	}

	toggleTimer() {
		this.props.toggleTimer();
		this.closeDropdown();
	}

	showShortcutModal() {
		const {formatMessage} = this.props.intl;

		this.setState({expanded: false});
		showModal({
			type: MODAL_TYPE.GENERIC,
			headerText: formatMessage({id: 'header.shortcuts'}),
			content: <ShortcutModalContent />,
		});
	}

	showWhatIsNewModal() {
		this.setState({expanded: false});
		tracking.trackElementClicked('whats new');
		showModal({
			type: MODAL_TYPE.WHAT_IS_NEW,
		});
	}

	closeDropdown() {
		this.setState({expanded: false});
	}

	toggleDropdown(e) {
		e.preventDefault();
		if (!this.state.expanded) {
			this.buttonRef.focus();
		}

		this.setState({expanded: !this.state.expanded});
	}

	handleBlur(e) {
		const newTarget = e.relatedTarget || e.explicitOriginalTarget || document.activeElement; // IE11
		if (
			newTarget &&
			newTarget.className &&
			(newTarget.className.includes('profile-menu-option') || newTarget.className.includes('profile-menu-link'))
		)
			return;
		this.setState({expanded: false});
	}

	handleKeyPress(e) {
		if (e.keyCode === 27 && this.state.expanded) {
			// Escape
			this.buttonRef.focus();
			this.setState({expanded: false});
		} else if (e.keyCode === 40) {
			// Down arrow
			if (!this.state.expanded) {
				this.setState({expanded: true, focusProfileRef: true});
			} else {
				this.focusNextInList();
			}
		} else if (e.keyCode === 38 && this.state.expanded) {
			// Up arrow
			this.focusPreviousInList();
		} else if (e.keyCode === 13 || e.keyCode === 32) {
			// Enter / Space
			const focusedElement = document.activeElement;
			if (focusedElement !== this.buttonRef) {
				e.preventDefault();
				focusedElement.click();
			}
		}
	}

	focusNextInList() {
		const focusedElement = document.activeElement;
		if (focusedElement === this.buttonRef) {
			this.profileRef.focus();
			return;
		}
		const availableRefs = this.getAvailableRefs();
		for (var i = 0; i < availableRefs.length; i++) {
			const ref = availableRefs[i];
			if (ref === focusedElement) {
				if (i + 1 === availableRefs.length) {
					availableRefs[0].focus();
				} else {
					availableRefs[i + 1].focus();
				}
				return;
			}
		}
	}

	focusPreviousInList() {
		const focusedElement = document.activeElement;
		const availableRefs = this.getAvailableRefs();
		for (var i = 0; i < availableRefs.length; i++) {
			const ref = availableRefs[i];
			if (ref === focusedElement) {
				if (i === 0) {
					availableRefs[availableRefs.length - 1].focus();
				} else {
					availableRefs[i - 1].focus();
				}
				return;
			}
		}
	}

	getAvailableRefs() {
		const availableRefs = [this.profileRef];
		if (this.accountRef !== undefined) availableRefs.push(this.accountRef);
		availableRefs.push(this.helpRef);
		availableRefs.push(this.showTimerRef);
		availableRefs.push(this.shortcutsRef);
		availableRefs.push(this.whatIsNewRef);
		availableRefs.push(this.logoutRef);
		return availableRefs;
	}

	render() {
		return (
			<div className="profile-menu-wrapper" data-cy="profile-menu-button">
				<button
					className={'profile' + (this.state.expanded ? ' expanded' : '')}
					aria-haspopup="menu"
					aria-expanded={this.state.expanded}
					onMouseDown={this.toggleDropdown.bind(this)}
					onBlur={this.handleBlur.bind(this)}
					onKeyDown={this.handleKeyPress.bind(this)}
					ref={button => (this.buttonRef = button)}
				>
					<div className="hexagon">
						{this.props.viewer.profilePictureId ? (
							<img
								crossOrigin="use-credentials"
								alt=""
								src={profilePicSrc(this.props.viewer.profilePictureId)}
								height="34"
								width="34"
							/>
						) : (
							<PersonInitials initials={this.props.viewer.initials} size={INITIALS_SIZE.SMALL} />
						)}
					</div>
				</button>

				<ul
					className={'profile-dropdown' + (this.state.expanded ? ' expanded' : '')}
					aria-hidden={!this.state.expanded}
					aria-labelledby="create-new-btn"
					role="menu"
				>
					<Link
						className="profile-menu-link"
						onBlur={this.handleBlur.bind(this)}
						to="/my-profile/profile"
						onClick={this.closeDropdown.bind(this)}
						role="button"
						onKeyDown={this.handleKeyPress.bind(this)}
					>
						<li
							data-cy={'my-profile-option'}
							className="profile-menu-option my-profile"
							role="menuitem"
							tabIndex="-1"
							ref={li => (this.profileRef = li)}
						>
							<ProfileIcon />
							<HeaderWrapper>
								<FormattedMessage id="common.my-profile" />
							</HeaderWrapper>
						</li>
					</Link>
					{hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) ? (
						<Link
							className="profile-menu-link"
							to="/settings/details"
							onBlur={this.handleBlur.bind(this)}
							onClick={this.closeDropdown.bind(this)}
							role="button"
							onKeyDown={this.handleKeyPress.bind(this)}
						>
							<li
								data-cy="admin-option"
								className="profile-menu-option account"
								role="menuitem"
								tabIndex="-1"
								ref={li => (this.accountRef = li)}
							>
								<AdminIcon />
								<HeaderWrapper>
									<FormattedMessage id="header.admin" />
								</HeaderWrapper>
							</li>
						</Link>
					) : null}
					<a
						className="profile-menu-link"
						href="http://help.forecast.it"
						onClick={this.closeDropdown.bind(this)}
						onBlur={this.handleBlur.bind(this)}
						role="button"
						onKeyDown={this.handleKeyPress.bind(this)}
						target="_blank"
						rel="noopener noreferrer"
					>
						<li className="profile-menu-option help" role="menuitem" tabIndex="-1" ref={li => (this.helpRef = li)}>
							<HelpIcon />
							<HeaderWrapper>
								<FormattedMessage id="header.help" />
							</HeaderWrapper>
						</li>
					</a>
					{!isClientUser() && !hasFeatureFlag('no_time_reg') ? (
						<li
							className="profile-menu-option show-timer"
							role="menuitem"
							tabIndex="-1"
							onClick={this.toggleTimer.bind(this)}
							onKeyDown={this.handleKeyPress.bind(this)}
							onBlur={this.handleBlur.bind(this)}
							ref={li => (this.showTimerRef = li)}
						>
							<TimerIcon />
							<HeaderWrapper>
								{this.props.hideTimer ? (
									<FormattedMessage id="timer.show" />
								) : (
									<FormattedMessage id="timer.hide" />
								)}
							</HeaderWrapper>
						</li>
					) : null}
					<li
						className="profile-menu-option shortcuts"
						role="menuitem"
						tabIndex="-1"
						onClick={this.showShortcutModal.bind(this)}
						onKeyDown={this.handleKeyPress.bind(this)}
						onBlur={this.handleBlur.bind(this)}
						ref={li => (this.shortcutsRef = li)}
					>
						<KeyboardIcon />
						<HeaderWrapper>
							<FormattedMessage id="header.shortcuts" />
						</HeaderWrapper>
					</li>
					{hasFeatureFlag('what_is_new', this.props.viewer.availableFeatureFlags) && (
						<li
							className="profile-menu-option what-is-new"
							role="menuitem"
							tabIndex="-1"
							onClick={this.showWhatIsNewModal.bind(this)}
							onKeyDown={this.handleKeyPress.bind(this)}
							onBlur={this.handleBlur.bind(this)}
							ref={li => (this.whatIsNewRef = li)}
						>
							<WhatIsNewIcon />
							<HeaderWrapper>
								<FormattedMessage id="header.what_is_new" />
							</HeaderWrapper>
						</li>
					)}
					<li
						className="profile-menu-option logout"
						id="profile-menu-option-logout"
						role="menuitem"
						tabIndex="-1"
						onClick={this.handleLogout.bind(this)}
						onKeyDown={this.handleKeyPress.bind(this)}
						onBlur={this.handleBlur.bind(this)}
						ref={li => (this.logoutRef = li)}
					>
						<LogOffIcon />
						<HeaderWrapper>
							<FormattedMessage id="header.log-out" />
						</HeaderWrapper>
					</li>
				</ul>
			</div>
		);
	}
}

ProfileMenu.propTypes = {
	viewer: PropTypes.object.isRequired,
};

export const HeaderWrapper = Styled.div`
	flex: auto;
	padding-left: 10px;
`;

export default injectIntl(ProfileMenu);
